import { CERBERUS_STATS_FIELD_CONFIGS } from '@hcs/cerberus-stats';
import { ChartTimeSeriesLine } from '@hcs/design-system';
import { NullState } from '@hcs/design-system';
import { Stats } from '@hcs/design-system';
import { ExperienceFlag } from '@hcs/experience-flags';
import { Glossary } from '@hcs/glossary';
import { GLOSSARY_TERMS } from '@hcs/glossary';
import { ChartDimensions, ChartSizes, LineChart } from '@hcs/pdf/charts';
import { CERBERUS_STATS_FIELDS } from '@hcs/types';
import { ReportId } from '@hcs/types';
import { formatNumber } from '@hcs/utils';

import { useDaysOnMarketChartDocument } from '../../hooks/useDaysOnMarketChartDocument';

import styles from './DaysOnMarket.module.css';
const dataHcName = 'days-on-market-chart';
interface Props {
  reportId: ReportId;
  chartSize?: ChartSizes;
  chartDimensions?: ChartDimensions;
  className?: string;
  reverseTickRotation?: boolean;
}
const {
  label,
  d3FormatStr,
  // Not using Display yet since @john is actively refactoring
} = CERBERUS_STATS_FIELD_CONFIGS[CERBERUS_STATS_FIELDS.daysOnMarketMedian];
export const DaysOnMarketChart = ({
  reportId,
  chartSize,
  chartDimensions,
  className,
  reverseTickRotation = false,
}: Props) => {
  const { isLoading, data: daysOnMarketDoc } =
    useDaysOnMarketChartDocument(reportId);
  if (!isLoading && !daysOnMarketDoc?.data?.chart.data?.length) {
    return (
      <NullState
        title="Days on Market Unavailable"
        dataHcName={`${dataHcName}-null-state`}
        absCenter
      />
    );
  }
  return (
    <ExperienceFlag
      experienceFlagId="PEXP_REDESIGN"
      next={
        <ChartTimeSeriesLine
          rightAxisMargin={35}
          // setting +45 deg prevents first tick from being cut off in PEXP report page tab
          axisBottom={{ tickRotation: reverseTickRotation ? 45 : -40 }}
          dataHcName={dataHcName}
          isLoading={isLoading}
          chartData={[
            {
              id: 'days-on-market',
              data: daysOnMarketDoc?.data?.chart.data || [],
            },
          ]}
          title={label}
          d3FormatterForY={d3FormatStr}
          theme={{
            Container: className,
          }}
          compactTitle
          stats={[
            {
              label: 'This month last year',
              value: formatNumber(
                daysOnMarketDoc?.data?.chart.stats.median.lastYear,
              ),
              dataHcName: 'year-prev',
            },
            {
              label: 'This month',
              value: formatNumber(
                daysOnMarketDoc?.data?.chart.stats.median.current,
              ),
              dataHcName: 'year-current',
            },
          ]}
          statsStyle={styles.StatsMarginAdjust}
        />
      }
      now={
        <LineChart
          title={
            <>
              Days on Market - Sold or De-listed Properties{' '}
              <Glossary glossaryTerms={[GLOSSARY_TERMS.DaysOnMarket]} />
            </>
          }
          dataHcName={dataHcName}
          chartSize={chartSize}
          chartDimensions={chartDimensions}
          isLoading={isLoading}
          chartData={daysOnMarketDoc?.data?.chart.data}
          yAxis={{ label: 'Days on Market' }}
          xAxis={{}}
          contentBelow={
            <Stats
              dataHcName={`${dataHcName}-stats`}
              stats={[
                {
                  label: 'This month last year',
                  value: formatNumber(
                    daysOnMarketDoc?.data?.chart.stats.median.lastYear,
                  ),
                  dataHcName: 'year-prev',
                },
                {
                  label: 'This month',
                  value: formatNumber(
                    daysOnMarketDoc?.data?.chart.stats.median.current,
                  ),
                  dataHcName: 'year-current',
                },
              ]}
            />
          }
        />
      }
    />
  );
};
